import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage } from '@ui/auth/AuthPages';
import { PublicOnly, RequireAuth } from '@ui/auth/guards';
import { Button, PageLoader } from '@ui/components/ui';
import { CUSTOMER_APP_URL } from '@ui/lib/urls';
import { useAppDispatch } from '@ui/store';
import { useLogoutMutation, useMeQuery } from '@ui/store/authApi';
import { api } from '@ui/store/baseApi';
import { AdminShell } from '@/components/AdminShell';

/** The platform admin panel: its own app and domain, separate from the customer dashboard. */

const page = <T extends Record<string, React.ComponentType>>(load: () => Promise<T>, name: keyof T) => {
  const Component = lazy(() => load().then((m) => ({ default: m[name] as React.ComponentType })));
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

function NotAdmin() {
  const [logout] = useLogoutMutation();
  const dispatch = useAppDispatch();
  return (
    <div className="flex min-h-full flex-col items-center justify-center p-6 text-center">
      <ShieldAlert className="h-10 w-10 text-warn" />
      <h1 className="mt-4 text-xl font-semibold">This area is for platform administrators</h1>
      <p className="mt-1 max-w-sm text-sm text-muted">Your account does not have admin access. Your workspace is on the main site.</p>
      <div className="mt-6 flex gap-2">
        <Button onClick={async () => { await logout().unwrap().catch(() => undefined); dispatch(api.util.resetApiState()); window.location.assign('/login'); }}>Sign out</Button>
        <Button variant="primary" onClick={() => window.location.assign(CUSTOMER_APP_URL)}>Go to my workspace</Button>
      </div>
    </div>
  );
}

function RequireAdmin() {
  const { data } = useMeQuery();
  return data?.user.isPlatformAdmin ? <Outlet /> : <NotAdmin />;
}

const home = () => '/';

export const router = createBrowserRouter([
  { path: '/login', element: <PublicOnly home={home}><LoginPage title="Platform admin" subtitle="Sign in with your administrator account." destination={home} /></PublicOnly> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    path: '/',
    element: <RequireAuth><RequireAdmin /></RequireAuth>,
    children: [
      {
        element: <AdminShell />,
        children: [
          { index: true, element: page(() => import('@/features/admin/AdminOverview'), 'AdminOverview') },
          { path: 'organizations', element: page(() => import('@/features/admin/AdminOrganizations'), 'AdminOrganizations') },
          { path: 'organizations/:id', element: page(() => import('@/features/admin/AdminOrganizations'), 'AdminOrgDetail') },
          { path: 'billing', element: page(() => import('@/features/admin/AdminBilling'), 'AdminBilling') },
          { path: 'numbers', element: page(() => import('@/features/admin/AdminNumbers'), 'AdminNumbers') },
          { path: 'catalog', element: page(() => import('@/features/admin/AdminCatalog'), 'AdminCatalog') },
          { path: 'calls', element: page(() => import('@/features/admin/AdminMisc'), 'AdminCalls') },
          { path: 'users', element: page(() => import('@/features/admin/AdminMisc'), 'AdminUsers') },
          { path: 'audit', element: page(() => import('@/features/admin/AdminMisc'), 'AdminAudit') },
          { path: 'system', element: page(() => import('@/features/admin/AdminMisc'), 'AdminSystem') },
          { path: 'settings', element: page(() => import('@/features/admin/AdminMisc'), 'AdminSettings') },
          { path: 'profile', element: page(() => import('@ui/account/ProfilePage'), 'ProfilePage') }
        ]
      }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
]);
